import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import type { ExecutionTopologyPreview, RouteCreateExecutionValidationResult } from './executionTopologyHelpers';
import type { ImmutableExecutionSnapshot } from './immutableExecutionSnapshot';
import { findDuplicateStopUids, formatDuplicateStopUidMessage } from './routeStopsSavePipeline';

/** Module-level save lock — blocks topology recompute during async route create. */
let executionTopologySaveLockCount = 0;

export function acquireExecutionTopologySaveLock(): void {
  executionTopologySaveLockCount += 1;
}

export function releaseExecutionTopologySaveLock(): void {
  executionTopologySaveLockCount = Math.max(0, executionTopologySaveLockCount - 1);
}

export function isExecutionTopologySaveLocked(): boolean {
  return executionTopologySaveLockCount > 0;
}

export function computeExecutionTopologyFingerprint(
  preview: ExecutionTopologyPreview | null | undefined
): string | null {
  if (!preview) return null;
  const positions = Array.from(preview.positionByStopUid.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  const loadingScope = Array.from(preview.loadingPositionByStopUid.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  const unloadingByGroup = Array.from(preview.unloadingOptionsByGroupUid.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([uid, opts]) => [uid, opts.map((o) => `${o.stopUid}:${o.value}`).sort()]);
  return JSON.stringify({
    positions,
    loadingScope,
    unloadingByGroup,
    saveRowCount: preview.saveRows.length
  });
}

export function groupsStopPositionsEqual(
  a: DeliveryGroupDraft[],
  b: DeliveryGroupDraft[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ga = a[i];
    const gb = b[i];
    if (ga.uid !== gb.uid || ga.loadingPosition !== gb.loadingPosition) return false;
    if (ga.loadingStops.length !== gb.loadingStops.length) return false;
    for (let j = 0; j < ga.loadingStops.length; j += 1) {
      if (
        ga.loadingStops[j].uid !== gb.loadingStops[j].uid ||
        ga.loadingStops[j].position !== gb.loadingStops[j].position
      ) {
        return false;
      }
    }
    if (ga.unloadingStops.length !== gb.unloadingStops.length) return false;
    for (let j = 0; j < ga.unloadingStops.length; j += 1) {
      if (
        ga.unloadingStops[j].uid !== gb.unloadingStops[j].uid ||
        ga.unloadingStops[j].position !== gb.unloadingStops[j].position
      ) {
        return false;
      }
    }
  }
  return true;
}

type LegacyStopLike = {
  uid?: string;
  position?: number;
  type?: string;
  groupUid?: string | null;
  parentLoadingPosition?: number | null;
};

export function legacyStopsStructuralEqual(a: LegacyStopLike[], b: LegacyStopLike[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const sa = a[i];
    const sb = b[i];
    if (
      String(sa.uid ?? '') !== String(sb.uid ?? '') ||
      Number(sa.position ?? -1) !== Number(sb.position ?? -1) ||
      String(sa.type ?? '') !== String(sb.type ?? '') ||
      String(sa.groupUid ?? '') !== String(sb.groupUid ?? '') ||
      Number(sa.parentLoadingPosition ?? -1) !== Number(sb.parentLoadingPosition ?? -1)
    ) {
      return false;
    }
  }
  return true;
}

export function validateStableExecutionSnapshot(
  snapshot: ImmutableExecutionSnapshot
): RouteCreateExecutionValidationResult {
  const duplicateUids = findDuplicateStopUids(snapshot.saveRows.map((row) => row._stopUid));
  const duplicatePayloadUids = findDuplicateStopUids(
    snapshot.payloadStops.map((stop) => {
      const uid = stop.clientExecutionUid;
      return typeof uid === 'string' ? uid : null;
    })
  );
  const allDuplicates = Array.from(new Set([...duplicateUids, ...duplicatePayloadUids]));
  if (allDuplicates.length > 0) {
    return { ok: false, message: formatDuplicateStopUidMessage(allDuplicates) };
  }

  const validPositions = new Set(snapshot.positionByStopUid.values());
  const loadingPositions = new Set(snapshot.loadingStops.map((s) => s.position));
  const edges = snapshot.allocationLinks;

  if (snapshot.allocations.some((a) => a.quantity > 0) && edges.length === 0) {
    return {
      ok: false,
      message: 'Execution topology inconsistent: отсутствуют execution edges'
    };
  }

  const seenEdgeIds = new Set<string>();
  for (const link of edges) {
    const edgeId = `${link.fromLoadingPosition}->${link.toUnloadingPosition}`;
    if (seenEdgeIds.has(edgeId)) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: duplicated execution edge ids'
      };
    }
    seenEdgeIds.add(edgeId);

    if (
      link.fromLoadingPosition == null ||
      link.toUnloadingPosition == null ||
      !Number.isFinite(link.fromLoadingPosition) ||
      !Number.isFinite(link.toUnloadingPosition)
    ) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: invalid from/to stop positions'
      };
    }

    if (
      !validPositions.has(link.fromLoadingPosition) ||
      !validPositions.has(link.toUnloadingPosition)
    ) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: invalid fromStopId or toStopId'
      };
    }
  }

  for (const alloc of snapshot.allocations) {
    if (alloc.quantity <= 0) continue;
    if (!alloc.fromStopUid?.trim() || !alloc.toStopUid?.trim()) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: undefined executionStopUid'
      };
    }
    if (
      !snapshot.positionByStopUid.has(alloc.fromStopUid) ||
      !snapshot.positionByStopUid.has(alloc.toStopUid)
    ) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: stop uid not in topology'
      };
    }
  }

  if (snapshot.loadingStops.length > 0 && loadingPositions.size === 0) {
    return {
      ok: false,
      message: 'Execution topology inconsistent: empty loading scopes'
    };
  }

  for (const ls of snapshot.loadingStops) {
    if (!ls.stopUid?.trim() || ls.position == null) {
      return {
        ok: false,
        message: 'Execution topology inconsistent: empty loading scope'
      };
    }
  }

  if (snapshot.payloadStops.length === 0) {
    return {
      ok: false,
      message: 'Execution topology inconsistent: empty stop payload'
    };
  }

  return { ok: true };
}

export function freezeStableExecutionSnapshot(
  snapshot: ImmutableExecutionSnapshot
): ImmutableExecutionSnapshot {
  const saveRows = snapshot.saveRows.map((row) => ({ ...row }));
  const loadingStops = snapshot.loadingStops.map((s) => Object.freeze({ ...s }));
  const unloadingStops = snapshot.unloadingStops.map((s) => Object.freeze({ ...s }));
  const allocations = snapshot.allocations.map((a) => Object.freeze({ ...a }));
  const allocationLinks = snapshot.allocationLinks.map((l) => ({ ...l }));
  const payloadStops = snapshot.payloadStops.map((row) => ({ ...row }));

  const positionByStopUid = new Map(snapshot.positionByStopUid);
  const stopUidByPosition = new Map(snapshot.stopUidByPosition);

  return Object.freeze({
    frozenAt: snapshot.frozenAt,
    loadingStops: Object.freeze(loadingStops),
    unloadingStops: Object.freeze(unloadingStops),
    allocations: Object.freeze(allocations),
    allocationLinks: Object.freeze(allocationLinks),
    saveRows: Object.freeze(saveRows),
    payloadStops: Object.freeze(payloadStops),
    positionByStopUid,
    stopUidByPosition
  });
}
